import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom, fromEvent, takeUntil, timeout, timer } from 'rxjs';
import { getTileImages, Tile } from '../types/tile.types';

export type TileImageSlot = 'icon' | 'cover';
export interface TileImageCapabilities { available: boolean; model: string; dailyLimit: number }
export interface MissingTileImages {
    tileId: string;
    name: string;
    color: string;
    /** Slots, die erzeugt werden (fehlende plus die zum Ersetzen gewählten). */
    missing: TileImageSlot[];
    /** Davon Slots mit vorhandenem Bild, das beim Generieren überschrieben wird. */
    replaces: TileImageSlot[];
    prompts: Partial<Record<TileImageSlot, string>>;
}
interface TileImageJob {
    id: string;
    status: 'submitting' | 'queued' | 'completed' | 'failed' | 'cancelled';
    imageUrl?: string;
    message?: string;
}

@Injectable({ providedIn: 'root' })
export class TileImageService {
    private readonly http = inject(HttpClient);
    private readonly base = '/api/tile/image-generation';

    public capabilities(): Promise<TileImageCapabilities> {
        return firstValueFrom(this.http.get<TileImageCapabilities>(`${this.base}/capabilities`).pipe(timeout(20000)));
    }

    /** Superadmin: globale Kacheln ohne Titelbild/Icon samt vorgeschlagenen Prompts; mit `replace`
     * auch Kacheln, deren vorhandenes Icon/Titelbild neu generiert werden soll. */
    public async missing(replace: TileImageSlot[] = []): Promise<MissingTileImages[]> {
        const query = replace.length ? `?replace=${replace.join(',')}` : '';
        return (await firstValueFrom(this.http.get<{ tiles: MissingTileImages[] }>(`${this.base}/missing${query}`).pipe(timeout(30000)))).tiles;
    }

    /** Superadmin: erzeugt die fehlenden (und mit `replace` auch vorhandene) Bilder einer Kachel und speichert sie direkt. */
    public fill(tileId: string, prompts: Partial<Record<TileImageSlot, string>>, replace: TileImageSlot[] = []): Promise<{ generated: TileImageSlot[] }> {
        return firstValueFrom(this.http.post<{ generated: TileImageSlot[] }>(`${this.base}/fill`, { tileId, prompts, replace }).pipe(timeout(300000)));
    }

    public async prepareUpload(file: Blob, slot: TileImageSlot): Promise<string> {
        const result = await firstValueFrom(this.http.post<{ imageUrl: string }>(`${this.base}/prepare-upload?slot=${slot}`, file).pipe(timeout(30000)));
        return result.imageUrl;
    }

    public async generate(prompt: string, slot: TileImageSlot, tileId: string | undefined, signal: AbortSignal): Promise<string> {
        let job: TileImageJob | undefined;
        let completed = false;
        try {
            signal.throwIfAborted();
            job = await firstValueFrom(this.http.post<TileImageJob>(`${this.base}/jobs`, {
                prompt, slot, ...(tileId ? { tileId } : {}), idempotencyKey: crypto.randomUUID(),
            }).pipe(timeout(30000)));
            const deadline = Date.now() + 130000;
            while (true) {
                signal.throwIfAborted();
                if (job.status === 'completed' && job.imageUrl) { completed = true; return job.imageUrl; }
                if (job.status === 'failed' || job.status === 'cancelled') throw new Error(job.message || $localize`:@@tileGenerationFailed:Die Bildgenerierung wurde beendet. Bitte erneut versuchen.`);
                if (Date.now() > deadline) throw new Error($localize`:@@tileGenerationTimeout:Die Bildgenerierung hat zu lange gedauert.`);
                await firstValueFrom(timer(1200).pipe(takeUntil(fromEvent(signal, 'abort'))));
                signal.throwIfAborted();
                job = await firstValueFrom(this.http.get<TileImageJob>(`${this.base}/jobs/${encodeURIComponent(job.id)}`).pipe(timeout(20000)));
            }
        } finally {
            if (job && !completed) {
                await firstValueFrom(this.http.delete(`${this.base}/jobs/${encodeURIComponent(job.id)}`).pipe(timeout(20000))).catch(() => { });
            }
        }
    }

    public async candidate(tile: Partial<Tile>, slot: TileImageSlot, imageUrl: string, prompt: string): Promise<Partial<Tile>> {
        const images = getTileImages(tile);
        const result = { ...tile, ...images, imageUrl: '' };
        if (slot === 'icon') { result.iconImageUrl = imageUrl; result.iconImagePrompt = prompt; }
        else { result.coverImageUrl = imageUrl; result.coverImagePrompt = prompt; }
        if (result.iconImageUrl.length + result.coverImageUrl.length > 180000) {
            const otherSlot = slot === 'icon' ? 'cover' : 'icon';
            const otherUrl = otherSlot === 'icon' ? result.iconImageUrl : result.coverImageUrl;
            const decoded = atob(otherUrl.slice(otherUrl.indexOf(',') + 1));
            const bytes = Uint8Array.from(decoded, character => character.charCodeAt(0));
            const prepared = await this.prepareUpload(new Blob([bytes], { type: 'application/octet-stream' }), otherSlot);
            if (otherSlot === 'icon') result.iconImageUrl = prepared;
            else result.coverImageUrl = prepared;
        }
        if (slot === 'cover' && result.imageMode !== 'cover') result.imageMode = 'cover-icon';
        if (slot === 'icon' && result.imageMode === 'cover') result.imageMode = 'cover-icon';
        return result;
    }
}
